import { useAtom } from "jotai";
import { Fragment, useState } from "react";
import Button from "components/base/Button";
import Backdrop from "components/common/Backdrop";
import { user_info } from "jotai/UserJotaiInstance";
import { Col, ListGroup, Row, Modal, Form, Spinner, Alert } from "react-bootstrap";
import useConnectedEmails from "hooks/useConnectedEmails";
import { faGoogle, faMicrosoft, faYahoo } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useUnauthorizedObserver from "hooks/useUnauthorizedObserver";
import { UserInfo } from "interfaces/user";
import axiosInstance from "helpers/api";

const ConnectNewEmail = () => {
  const [userInfo] = useAtom<UserInfo>(user_info);
  const connectedEmails = useConnectedEmails();
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<'google' | 'microsoft' | null>(null);

  // Yahoo / IMAP connect (app password — KOPS-611)
  const [showYahoo, setShowYahoo] = useState(false);
  const [yahooEmail, setYahooEmail] = useState("");
  const [yahooPassword, setYahooPassword] = useState("");
  const [yahooConsent, setYahooConsent] = useState(false);
  const [yahooLoading, setYahooLoading] = useState(false);
  const [yahooError, setYahooError] = useState<string | null>(null);
  const [yahooSuccess, setYahooSuccess] = useState<string | null>(null);

  useUnauthorizedObserver(isAuthorized);

  const handleShowConsent = (provider: 'google' | 'microsoft') => {
    setPendingProvider(provider);
    setConsentChecked(false);
    setShowConsent(true);
  };

  const handleConsentConfirm = () => {
    setShowConsent(false);
    if (pendingProvider === 'google') {
      doConnectGoogle();
    } else if (pendingProvider === 'microsoft') {
      doConnectMicrosoft();
    }
  };

  const handleConnectWithGoogle = () => {
    handleShowConsent('google');
  };

  const doConnectGoogle = () => {
    // Get auth token from localStorage
    const authToken = localStorage.getItem('auth_token');

    if (!authToken) {
      console.error('No auth token found in localStorage');
      alert('Please log in again to connect your Gmail account.');
      return;
    }

    // Use current origin for API calls - check hostname at runtime, not build time
    const baseUrl = window.location.hostname === 'localhost'
      ? "http://localhost:8080"
      : "";

    // Pass token as query parameter for OAuth flow
    window.location.href = `${baseUrl}/api/google/connect/auth?token=${encodeURIComponent(authToken)}`;
  };

  const handleConnectWithMicrosoft = () => {
    handleShowConsent('microsoft');
  };

  const doConnectMicrosoft = () => {
    // Get auth token from localStorage
    const authToken = localStorage.getItem('auth_token');

    if (!authToken) {
      console.error('No auth token found in localStorage');
      alert('Please log in again to connect your Microsoft account.');
      return;
    }

    // Use current origin for API calls - check hostname at runtime, not build time
    const baseUrl = window.location.hostname === 'localhost'
      ? "http://localhost:8080"
      : "";

    // Pass token as query parameter for OAuth flow
    window.location.href = `${baseUrl}/api/microsoft/connect/auth?token=${encodeURIComponent(authToken)}`;
  };

  const openYahoo = () => {
    setYahooEmail("");
    setYahooPassword("");
    setYahooConsent(false);
    setYahooError(null);
    setYahooSuccess(null);
    setShowYahoo(true);
  };

  const submitYahoo = async () => {
    setYahooError(null);
    if (!yahooEmail.trim() || !yahooPassword.trim()) {
      setYahooError("Enter your Yahoo email and the 16-character app password.");
      return;
    }
    setYahooLoading(true);
    try {
      const { data } = await axiosInstance.post("/connected-emails/connect-yahoo", {
        email: yahooEmail.trim(),
        app_password: yahooPassword,
        provider: "yahoo",
      });
      if (data?.success) {
        setYahooSuccess(
          `Connected! We found ${data.contactsCreated ?? 0} contacts and are downloading your emails to classify your book now.`
        );
        setYahooPassword("");
      } else {
        setYahooError(data?.error || "Could not connect. Double-check your email and app password.");
      }
    } catch (err: any) {
      setYahooError(
        err?.response?.data?.error ||
          "Could not connect. Make sure 2-step verification is on and you used a freshly generated app password."
      );
    } finally {
      setYahooLoading(false);
    }
  };

  const isGmailConnected = connectedEmails.some((email: { provider: string }) => email.provider === "gmail");
  const isMicrosoftConnected = connectedEmails.some((email: { provider: string }) => email.provider === "microsoft" || email.provider === "outlook");
  const isYahooConnected = connectedEmails.some((email: { provider: string }) => email.provider === "yahoo");

  return (
    <Fragment>
      <h3 className="mb-4">New Account</h3>
      <Row>
        <Col xl={6} xxl={4}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h6>New account</h6>
              <p className="mb-0">Connect a new account to your workspace</p>
            </div>
          </div>
          <ListGroup className="mb-4">
            <ListGroup.Item>Choose your provider</ListGroup.Item>
            <ListGroup.Item>
              <div className="d-flex flex-column gap-3 py-4">
                {/* Gmail Connection */}
                <div className="d-flex justify-content-center align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    {isGmailConnected ? (
                      <span className="text-muted">
                        <FontAwesomeIcon icon={faGoogle} className="me-2" />
                        You've already connected a Gmail account.
                      </span>
                    ) : (
                      <Button
                        onClick={handleConnectWithGoogle}
                        variant="phoenix-secondary"
                        startIcon={<FontAwesomeIcon icon={faGoogle} />}
                        className="px-4"
                      >
                        Connect with Google
                      </Button>
                    )}
                  </div>
                </div>

                {/* Microsoft Connection */}
                <div className="d-flex justify-content-center align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    {isMicrosoftConnected ? (
                      <span className="text-muted">
                        <FontAwesomeIcon icon={faMicrosoft} className="me-2" />
                        You've already connected a Microsoft account.
                      </span>
                    ) : (
                      <Button
                        onClick={handleConnectWithMicrosoft}
                        variant="phoenix-secondary"
                        startIcon={<FontAwesomeIcon icon={faMicrosoft} />}
                        className="px-4"
                      >
                        Connect with Microsoft
                      </Button>
                    )}
                  </div>
                </div>

                {/* Yahoo Connection (app password — KOPS-611) */}
                <div className="d-flex justify-content-center align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    {isYahooConnected ? (
                      <span className="text-muted">
                        <FontAwesomeIcon icon={faYahoo} className="me-2" />
                        You've already connected a Yahoo account.
                      </span>
                    ) : (
                      <Button
                        onClick={openYahoo}
                        variant="phoenix-secondary"
                        startIcon={<FontAwesomeIcon icon={faYahoo} />}
                        className="px-4"
                      >
                        Connect with Yahoo
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </ListGroup.Item>
          </ListGroup>
        </Col>
      </Row>
      {!isAuthorized && <Backdrop />}

      {/* Data Usage Consent Modal */}
      <Modal show={showConsent} onHide={() => setShowConsent(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #1F3864' }}>
          <Modal.Title style={{ color: '#1F3864', fontWeight: 'bold' }}>
            Email Data Usage Disclosure
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>By connecting your email account, you authorize KairosIQ to:</p>
          <ul>
            <li><strong>Read and classify</strong> your email contacts to identify leads, past clients, and professional relationships</li>
            <li><strong>Analyze email content</strong> to build lead profiles, determine relationship stages, and generate AI coaching recommendations</li>
            <li><strong>Store email data</strong> encrypted (AES-256-GCM) within our private cloud infrastructure</li>
            <li><strong>Use de-identified, aggregated patterns</strong> from your interactions to improve our AI models for all users</li>
          </ul>
          <p><strong>Your data protection:</strong></p>
          <ul>
            <li>Email content is encrypted at rest and in transit</li>
            <li>Personal information is de-identified before any AI model training</li>
            <li>Your data never leaves our private AWS infrastructure</li>
            <li>We never share individual email content with third parties</li>
            <li>You can disconnect your email and request data deletion at any time</li>
          </ul>
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            For full details, see our{' '}
            <a href="/privacy-policy" target="_blank">Privacy Policy</a> and{' '}
            <a href="/ai-ethics" target="_blank">AI Ethics Policy</a>.
          </p>
          <Form.Check
            type="checkbox"
            id="dataConsentCheck"
            label="I understand and consent to the use of my email data as described above"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            className="mt-3"
            style={{ fontWeight: 'bold' }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="phoenix-secondary" onClick={() => setShowConsent(false)}>
            Cancel
          </Button>
          <Button
            variant="phoenix-primary"
            onClick={handleConsentConfirm}
            disabled={!consentChecked}
          >
            Connect {pendingProvider === 'google' ? 'Gmail' : 'Microsoft'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Yahoo Connect Modal (app password) */}
      <Modal show={showYahoo} onHide={() => setShowYahoo(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #6001D2' }}>
          <Modal.Title style={{ color: '#6001D2', fontWeight: 'bold' }}>
            <FontAwesomeIcon icon={faYahoo} className="me-2" />
            Connect Yahoo Mail
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {yahooSuccess ? (
            <Alert variant="success" className="mb-0">{yahooSuccess}</Alert>
          ) : (
            <Fragment>
              <p className="mb-2">
                Yahoo needs a one-time <strong>app password</strong> (not your normal password).
                It's secure and you can revoke it anytime.
              </p>
              <ol style={{ fontSize: '0.9rem' }}>
                <li>Go to{' '}
                  <a href="https://login.yahoo.com/myaccount/security" target="_blank" rel="noreferrer">
                    Yahoo Account Security
                  </a>{' '}(turn on 2-step verification if needed)</li>
                <li>Click <strong>Generate app password</strong>, name it <strong>KairosIQ</strong></li>
                <li>Copy the 16-character password and paste it below</li>
              </ol>
              <Form.Group className="mb-3">
                <Form.Label>Yahoo email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="you@yahoo.com"
                  value={yahooEmail}
                  onChange={(e) => setYahooEmail(e.target.value)}
                  disabled={yahooLoading}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>App password (16 characters)</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="xxxx xxxx xxxx xxxx"
                  value={yahooPassword}
                  onChange={(e) => setYahooPassword(e.target.value)}
                  disabled={yahooLoading}
                  autoComplete="off"
                />
                <Form.Text className="text-muted">
                  Stored AES-256 encrypted — never your main password, revocable anytime.
                </Form.Text>
              </Form.Group>
              <Form.Check
                type="checkbox"
                id="yahooConsent"
                label="I consent to KairosIQ reading and classifying my email to build my book (encrypted, private cloud)."
                checked={yahooConsent}
                onChange={(e) => setYahooConsent(e.target.checked)}
                className="mb-2"
              />
              {yahooError && <Alert variant="danger" className="mt-2 mb-0 py-2">{yahooError}</Alert>}
            </Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="phoenix-secondary" onClick={() => setShowYahoo(false)}>
            {yahooSuccess ? "Close" : "Cancel"}
          </Button>
          {!yahooSuccess && (
            <Button
              variant="phoenix-primary"
              onClick={submitYahoo}
              disabled={yahooLoading || !yahooConsent}
            >
              {yahooLoading ? (
                <Fragment><Spinner size="sm" animation="border" className="me-2" />Connecting…</Fragment>
              ) : "Connect Yahoo"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default ConnectNewEmail;
